import React from "react";
import {
  Box,
  Typography,
  Paper,
  Grid,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { ArrowBack, CheckCircle, Visibility, ArrowForward } from "@mui/icons-material";
import { IMasterFoodMenu } from "../../../../redux/food/food.types";
import { RightPanel } from "../../../../components/RightPanel";
import { withS3Prefix } from "../../../../helpers/withS3Prefix";
import dummyImage from "../../../../assets/dummy_image.jpg";

interface IMasterMenuSelectionStepProps {
  masterMenus: IMasterFoodMenu[];
  loading: boolean;
  existingMenuTitle?: string;
  currentMasterMenuUuid?: string | null;
  hasExistingMenu: boolean;
  onSelectMasterMenu: (menu: IMasterFoodMenu) => void;
  onContinueExisting: () => void;
  onBack?: () => void;
}


export const MasterMenuSelectionStep: React.FC<IMasterMenuSelectionStepProps> = ({
  masterMenus,
  loading,
  existingMenuTitle,
  currentMasterMenuUuid,
  hasExistingMenu,
  onSelectMasterMenu,
  onContinueExisting,
  onBack,
}) => {
  const [confirmDialog, setConfirmDialog] = React.useState(false);
  const [pendingMenu, setPendingMenu] = React.useState<IMasterFoodMenu | null>(null);
  const [previewMenu, setPreviewMenu] = React.useState<IMasterFoodMenu | null>(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const handleMenuClick = (menu: IMasterFoodMenu) => {
    if (hasExistingMenu && menu.master_menu_uuid !== currentMasterMenuUuid) {
      setPendingMenu(menu);
      setConfirmDialog(true);
    } else if (hasExistingMenu && menu.master_menu_uuid === currentMasterMenuUuid) {
      onContinueExisting();
    } else {
      onSelectMasterMenu(menu);
    }
  };

  const handleConfirm = () => {
    if (pendingMenu) {
      onSelectMasterMenu(pendingMenu);
    }
    setPendingMenu(null);
    setConfirmDialog(false);
  };

  const handleCancel = () => {
    setPendingMenu(null);
    setConfirmDialog(false);
  };

  // Sort menus: current master menu first
  const sortedMenus = React.useMemo(() => {
    if (!currentMasterMenuUuid) return masterMenus;
    return [...masterMenus].sort((a, b) => {
      if (a.master_menu_uuid === currentMasterMenuUuid) return -1;
      if (b.master_menu_uuid === currentMasterMenuUuid) return 1;
      return 0;
    });
  }, [masterMenus, currentMasterMenuUuid]);

  if (loading) {
    return (
      <Box display="flex" alignItems="center" justifyContent="center" height="50vh">
        <CircularProgress size={40} />
      </Box>
    );
  }

  return (
    <Box sx={{ px: 2.5, height: "100%", overflow: "auto", pt: 1, pb: 5 }}>
      {/* Back button */}
      {onBack && (
        <Box sx={{ mb: 1 }}>
          <Button
            startIcon={<ArrowBack sx={{ fontSize: 16 }} />}
            onClick={onBack}
            size="small"
            sx={{ textTransform: "none", color: "#64748B", fontSize: "0.82rem", fontWeight: 600 }}
          >
            Back
          </Button>
        </Box>
      )}

      {/* Heading */}
      <Typography sx={{ fontSize: "2rem", fontWeight: 700, lineHeight: 1.15, letterSpacing: -0.5, mb: 0.75 }}>
        Select a Master Menu
      </Typography>
      <Typography sx={{ fontSize: "0.9rem", color: "text.secondary", mb: 2.5, lineHeight: 1.4 }}>
        Choose a menu template to start building the food menu.
      </Typography>

      {/* Continue with existing */}
      {hasExistingMenu && (
        <Paper
          elevation={0}
          onClick={onContinueExisting}
          sx={{
            p: 2,
            mb: 3,
            border: "2px solid #1a97f5",
            borderRadius: "14px",
            bgcolor: "#e6f4ff",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            transition: "all 0.2s",
            "&:hover": {
              boxShadow: "0 4px 16px rgba(26, 151, 245, 0.2)",
            },
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <CheckCircle sx={{ color: "#1a97f5", fontSize: 24 }} />
            <Box>
              <Typography fontWeight={700} color="#1E293B">
                Continue with existing menu
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {existingMenuTitle || "Previously saved selections"}
              </Typography>
            </Box>
          </Box>
          <Chip label="Saved" size="small" color="primary" />
        </Paper>
      )}

      {/* Master menu cards */}
      <Grid container spacing={2.5}>
        {sortedMenus.map((menu, index) => {
          const isCurrent = menu.master_menu_uuid === currentMasterMenuUuid;
          const isFixed = menu.is_fixed === true || menu.is_fixed === 1;

          const itemCount = menu.menu_json?.reduce((sum, cat) => {
            return sum + (cat.food_items?.filter((item: any) => {
              if (isFixed) return item.isSelected === true;
              return item.isSelected !== undefined ? item.isSelected === true : true;
            }).length || 0);
          }, 0) || 0;

          const categoryCount = menu.menu_json?.filter((cat) => {
            return cat.food_items?.some((item: any) => {
              if (isFixed) return item.isVisible !== false;
              return item.isSelected !== undefined ? item.isSelected === true : true;
            });
          }).length || 0;
          const menuImage = (() => {
            const attachment = menu.attachments?.[0];
            if (!attachment?.path) return dummyImage;
            return withS3Prefix(attachment.path);
          })();
          const menuTags = menu.tags ?? [];

          return (
            <Grid item xs={12} sm={4} md={2.4} lg={2} key={menu.master_menu_uuid}>
              <Paper
                elevation={0}
                onClick={() => handleMenuClick(menu)}
                sx={{
                  borderRadius: "16px",
                  overflow: "hidden",
                  border: isCurrent ? "2px solid #1a97f5" : "1px solid #E2E8F0",
                  cursor: "pointer",
                  transition: "all 0.2s ease",
                  "&:hover": {
                    boxShadow: "0 8px 24px rgba(0,0,0,0.1)",
                    transform: "translateY(-3px)",
                  },
                }}
              >
                {/* Image Section */}
                <Box
                  sx={{
                    position: "relative",
                    height: 160,
                    bgcolor: "#1E293B",
                    backgroundImage: `url(${menuImage})`,
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                  }}
                >
                  {/* Tags badges */}
                  {menuTags.length > 0 && (
                    <Box sx={{ position: "absolute", top: 12, left: 12, display: "flex", gap: 0.5, flexWrap: "wrap", maxWidth: "60%" }}>
                      {menuTags.slice(0, 2).map((tag) => (
                        <Chip
                          key={tag}
                          label={tag}
                          size="small"
                          sx={{
                            bgcolor: "#FFFFFF",
                            color: "#1E293B",
                            fontWeight: 700,
                            fontSize: "10px",
                            letterSpacing: "0.05em",
                            height: 24,
                          }}
                        />
                      ))}
                    </Box>
                  )}

                  {isCurrent && (
                    <Chip
                      label="CURRENT"
                      size="small"
                      sx={{
                        position: "absolute",
                        top: 12,
                        right: 12,
                        bgcolor: "#1a97f5",
                        color: "#FFFFFF",
                        fontWeight: 700,
                        fontSize: "10px",
                        height: 24,
                      }}
                    />
                  )}

                  {/* Price overlay */}
                  {menu.price != null && menu.price > 0 && (
                    <Chip
                      label={`₹ ${menu.price} / person`}
                      size="small"
                      sx={{
                        position: "absolute",
                        bottom: 12,
                        left: 12,
                        bgcolor: "rgba(0,0,0,0.7)",
                        color: "#FFFFFF",
                        fontWeight: 600,
                        fontSize: "12px",
                        height: 28,
                        backdropFilter: "blur(4px)",
                      }}
                    />
                  )}

                  {/* Preview button - hidden for now */}
                  {/* <Chip
                    icon={<Visibility sx={{ fontSize: 14, color: "#1E293B" }} />}
                    label="Preview"
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      setPreviewMenu(menu);
                    }}
                    sx={{
                      position: "absolute",
                      bottom: 12,
                      right: 12,
                      bgcolor: "rgba(255,255,255,0.9)",
                      color: "#1E293B",
                      fontWeight: 600,
                      fontSize: "12px",
                      height: 28,
                      backdropFilter: "blur(4px)",
                      cursor: "pointer",
                    }}
                  /> */}
                </Box>

                {/* Info Section */}
                <Box sx={{ p: 2, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                  <Box>
                    <Typography sx={{ fontWeight: 700, fontSize: "16px", color: "#1E293B" }}>
                      {menu.title}
                    </Typography>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.5 }}>
                      <Box
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: "50%",
                          bgcolor: "#1a97f5",
                        }}
                      />
                      <Typography variant="body2" color="text.secondary">
                        {categoryCount} courses · {itemCount} items
                      </Typography>
                    </Box>
                  </Box>

                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    <Typography
                      sx={{
                        fontSize: "11px",
                        fontWeight: 700,
                        letterSpacing: "0.05em",
                        textTransform: "uppercase",
                        color: "#1E293B",
                      }}
                    >
                      {isCurrent ? "CONTINUE" : "SELECT"}
                    </Typography>
                    <ArrowForward sx={{ fontSize: 16, color: "#1E293B" }} />
                  </Box>
                </Box>
              </Paper>
            </Grid>
          );
        })}
      </Grid>

      {masterMenus.length === 0 && !loading && (
        <Box sx={{ textAlign: "center", py: 8 }}>
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No master menus available
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Create a master menu first to use it as a template.
          </Typography>
        </Box>
      )}

      {/* Confirmation Dialog */}
      <Dialog open={confirmDialog} onClose={handleCancel}>
        <DialogTitle>Switch Master Menu?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Switching to a different master menu will discard all current selections and replace them with the new menu's items and limits. This cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancel}>Cancel</Button>
          <Button onClick={handleConfirm} variant="contained" color="primary">
            Confirm Switch
          </Button>
        </DialogActions>
      </Dialog>

      {/* Menu Preview — Dialog on mobile, RightPanel on desktop */}
      {isMobile ? (
        <Dialog
          open={!!previewMenu}
          onClose={() => setPreviewMenu(null)}
          maxWidth="sm"
          fullWidth
        >
          {previewMenu && (
            <>
              <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <Box>
                  <Typography sx={{ fontWeight: 700, fontSize: "18px" }}>
                    {previewMenu.title}
                  </Typography>
                  {previewMenu.price != null && previewMenu.price > 0 && (
                    <Typography variant="body2" color="text.secondary">
                      ₹ {previewMenu.price} / person
                    </Typography>
                  )}
                </Box>
                <Chip
                  label={`${previewMenu.menu_json?.length || 0} courses`}
                  size="small"
                  color="primary"
                  variant="outlined"
                />
              </DialogTitle>
              <DialogContent dividers sx={{ maxHeight: "60vh" }}>
                {previewMenu.menu_json?.map((category) => (
                  <Box key={category.category_uuid} sx={{ mb: 2.5 }}>
                    <Typography sx={{ fontWeight: 700, fontSize: "15px", color: "#1E293B", mb: 1 }}>
                      {category.category_name}
                    </Typography>
                    {category.subcategories && category.subcategories.length > 0 ? (
                      category.subcategories.map((sub) => {
                        const subItems = category.food_items?.filter(
                          (item) => item.sub_category_uuid === sub.food_subcategory_name_uuid
                        ) || sub.food_items || [];
                        if (subItems.length === 0) return null;
                        return (
                          <Box key={sub.food_subcategory_name_uuid} sx={{ ml: 2, mb: 1.5 }}>
                            <Typography sx={{ fontWeight: 600, fontSize: "13px", color: "#64748B", mb: 0.5 }}>
                              {sub.subcategory_name}
                            </Typography>
                            {subItems.map((item) => (
                              <Box key={item.food_item_uuid} sx={{ display: "flex", alignItems: "center", py: 0.4, ml: 1 }}>
                                <Box sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: "#1a97f5", mr: 1.25, flexShrink: 0 }} />
                                <Typography sx={{ fontSize: "13px", color: "#1E293B" }}>
                                  {item.item_name}
                                </Typography>
                              </Box>
                            ))}
                          </Box>
                        );
                      })
                    ) : (
                      category.food_items?.map((item) => (
                        <Box key={item.food_item_uuid} sx={{ display: "flex", alignItems: "center", py: 0.4, ml: 2 }}>
                          <Box sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: "#1a97f5", mr: 1.25, flexShrink: 0 }} />
                          <Typography sx={{ fontSize: "13px", color: "#1E293B" }}>
                            {item.item_name}
                          </Typography>
                        </Box>
                      ))
                    )}
                  </Box>
                ))}
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setPreviewMenu(null)}>Close</Button>
                <Button
                  variant="contained"
                  onClick={() => {
                    setPreviewMenu(null);
                    handleMenuClick(previewMenu);
                  }}
                >
                  Select This Menu
                </Button>
              </DialogActions>
            </>
          )}
        </Dialog>
      ) : (
        <RightPanel
          open={!!previewMenu}
          heading={previewMenu?.title || "Menu Preview"}
          subHeading={
            previewMenu?.price != null && previewMenu.price > 0
              ? `₹ ${previewMenu.price} / person`
              : undefined
          }
          width="420px"
          onClose={() => setPreviewMenu(null)}
          topRightButtons={
            <Chip
              label={`${previewMenu?.menu_json?.length || 0} courses`}
              size="small"
              color="primary"
              variant="outlined"
            />
          }
          actionButtons={() => (
            <Box sx={{ display: "flex", gap: 1, px: 2, py: 1 }}>
              <Button
                variant="outlined"
                fullWidth
                onClick={() => setPreviewMenu(null)}
              >
                Close
              </Button>
              <Button
                variant="contained"
                fullWidth
                onClick={() => {
                  if (previewMenu) {
                    setPreviewMenu(null);
                    handleMenuClick(previewMenu);
                  }
                }}
              >
                Select This Menu
              </Button>
            </Box>
          )}
        >
          {previewMenu && (
            <Box sx={{ p: 2 }}>
              {previewMenu.menu_json?.map((category) => (
                <Box key={category.category_uuid} sx={{ mb: 2.5 }}>
                  <Typography sx={{ fontWeight: 700, fontSize: "15px", color: "#1E293B", mb: 1 }}>
                    {category.category_name}
                  </Typography>
                  {category.subcategories && category.subcategories.length > 0 ? (
                    category.subcategories.map((sub) => {
                      const subItems = category.food_items?.filter(
                        (item) => item.sub_category_uuid === sub.food_subcategory_name_uuid
                      ) || sub.food_items || [];
                      if (subItems.length === 0) return null;
                      return (
                        <Box key={sub.food_subcategory_name_uuid} sx={{ ml: 2, mb: 1.5 }}>
                          <Typography sx={{ fontWeight: 600, fontSize: "13px", color: "#64748B", mb: 0.5 }}>
                            {sub.subcategory_name}
                          </Typography>
                          {subItems.map((item) => (
                            <Box key={item.food_item_uuid} sx={{ display: "flex", alignItems: "center", py: 0.4, ml: 1 }}>
                              <Box sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: "#1a97f5", mr: 1.25, flexShrink: 0 }} />
                              <Typography sx={{ fontSize: "13px", color: "#1E293B" }}>
                                {item.item_name}
                              </Typography>
                            </Box>
                          ))}
                        </Box>
                      );
                    })
                  ) : (
                    category.food_items?.map((item) => (
                      <Box key={item.food_item_uuid} sx={{ display: "flex", alignItems: "center", py: 0.4, ml: 2 }}>
                        <Box sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: "#1a97f5", mr: 1.25, flexShrink: 0 }} />
                        <Typography sx={{ fontSize: "13px", color: "#1E293B" }}>
                          {item.item_name}
                        </Typography>
                      </Box>
                    ))
                  )}
                </Box>
              ))}
            </Box>
          )}
        </RightPanel>
      )}
    </Box>
  );
};
